/**
 * 「我手上这一份还是不是线上那一份」。
 *
 * # 为什么需要它
 *
 * 2026-09-07 真踩到：一台手机上开着的标签页**连续 19.5 小时**跑着前一天的
 * bundle，其间上线过八次以上，而页面自己一次都不知道。
 *
 * 表现是最贵的那一种——**发布方说「已上线」，用户说「我这儿还是旧的」，
 * 两边都对**，而在这之前没有任何东西能让他们发现说的不是同一份。
 * 那一次为此来回查了血缘、制品、缓存头、service worker、访问日志，
 * 全都是对的；错的只是「谁都不知道页面过期了」。
 *
 * 服务端一直有 `/console-built-from`（部署的那一版），构建期一直有
 * `VITE_BUILD_COMMIT`（跑着的这一版）。**两边都在，只是没人比对。**
 */

/** 跑着的这一份是什么版本。构建期由 Dockerfile.console 注入。 */
export function runningBuild(): string {
  const raw = import.meta.env.VITE_BUILD_COMMIT
  return typeof raw === 'string' ? raw.trim() : ''
}

/**
 * 判「我该不该提醒他」。
 *
 * **两边都拿得到、且确实不同，才算过期。** 三种「不知道」一律当作没过期：
 *
 *   - 本地不知道自己是哪一版（开发模式、没注入 COMMIT）
 *   - 服务端没回（网断了、接口 404）
 *   - 回来的不是一个像样的 commit（SPA 兜底会把 index.html 回给任何路径，
 *     那是一整页 HTML，不是 SHA）
 *
 * 判据宁可漏报也不误报：一条「你这份旧了」的假提醒，会让他去刷新，
 * 刷完还是这一版——**那比不提醒更伤信任**。
 */
export function isStale(running: string, deployed: string): boolean {
  if (!looksLikeCommit(running) || !looksLikeCommit(deployed)) return false
  return running !== deployed
}

function looksLikeCommit(value: string): boolean {
  return /^[0-9a-f]{40}$/.test(value.trim())
}

/** 去问服务端现在部署的是哪一版。拿不到就回空串——**不猜**。 */
export async function fetchDeployedBuild(
  fetchImpl: typeof fetch = fetch,
  path = '/console-built-from',
): Promise<string> {
  try {
    // cache: 'no-store'：这条问的就是「现在」，走缓存等于问了个寂寞。
    const response = await fetchImpl(path, { cache: 'no-store' })
    if (!response.ok) return ''
    const text = (await response.text()).trim()
    return looksLikeCommit(text) ? text : ''
  } catch {
    return ''
  }
}
